import React, { useState, useEffect, useRef, useCallback } from 'react';
import './MarketingHubModal.css';

const API = '/api/opo/marketing';

// ── Tier colours ────────────────────────────────────────────────────────────
const TIER_COLOUR = { 0: '#f59e0b', 1: '#34d399', 2: '#60a5fa', 3: '#a78bfa' };
const TIER_LABEL  = { 0: 'Coordinator', 1: 'Channel & Production', 2: 'Reach & Lifecycle', 3: 'Strategy & Measurement' };

const TABS = [
  { id: 'agents',    label: 'Agents' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'assets',    label: 'Assets' },
  { id: 'chat',      label: 'Chat' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function mktFetch(url, opts = {}) {
  const hasBody = opts.body != null && !(opts.body instanceof FormData);
  return fetch(url, {
    cache: 'no-store',
    credentials: 'include',
    ...opts,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(opts.headers || {}),
    },
  });
}

function fmtDuration(sec) {
  if (!sec) return '—';
  const m = Math.floor(sec / 60), s = Math.round(sec % 60);
  return `${m}m ${s}s`;
}

function fmtBytes(b) {
  if (!b) return '—';
  if (b > 1e6) return (b / 1e6).toFixed(1) + ' MB';
  return (b / 1e3).toFixed(0) + ' KB';
}

function fmtCompactNum(n) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—';
  const x = Number(n);
  if (x >= 1e6) return (x / 1e6).toFixed(1) + 'M';
  if (x >= 1e3) return (x / 1e3).toFixed(1) + 'K';
  return String(Math.round(x));
}

function fmtPlayTime(sec) {
  const s = Math.floor(Number(sec) || 0);
  if (s <= 0) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${m}m ${r}s`;
  if (m > 0) return `${m}m ${r}s`;
  return `${r}s`;
}

function prettifySummaryText(text) {
  const raw = String(text || '');
  if (!raw.trim()) return '';
  return raw
    // Remove markdown heading markers
    .replace(/^\s{0,3}#{1,6}\s*/gm, '')
    // Remove bold/italic markers
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    // Remove inline code markers
    .replace(/`([^`]*)`/g, '$1')
    // Normalize repeated blank lines
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function parseSummaryJson(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  try { return JSON.parse(raw); } catch (_) {}
  const m = raw.match(/\{[\s\S]*\}/);
  if (!m) return null;
  try { return JSON.parse(m[0]); } catch (_) { return null; }
}

function normalizeStats(raw) {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch (_) { return {}; }
  }
  return raw;
}

function pickSeries(stats) {
  const s = normalizeStats(stats);
  if (Array.isArray(s.views_by_day) && s.views_by_day.length) return { yKey: 'views', points: s.views_by_day };
  if (Array.isArray(s.series_impressions) && s.series_impressions.length) {
    return { yKey: 'impressions', points: s.series_impressions };
  }
  return { yKey: 'views', points: [] };
}

/** Lightweight SVG sparkline; points: [{ date, views }] or [{ date, impressions }] */
function MiniLineChart({ points, yKey = 'views' }) {
  if (!points || points.length === 0) {
    return <p className="mkt-stat-muted">No time series in this snapshot (optional JSON: views_by_day or series_impressions).</p>;
  }
  const clean = points.map((p) => ({
    label: String(p.date || p.day || ''),
    v: Number(p[yKey] ?? p.views ?? p.impressions ?? p.v ?? 0),
  }));
  const max = Math.max(...clean.map((c) => c.v), 1);
  const w = 100;
  const h = 48;
  const pad = 6;
  const pts = clean.map((c, i) => {
    const x = pad + (i / Math.max(clean.length - 1, 1)) * (w - pad * 2);
    const y = h - pad - (c.v / max) * (h - pad * 2);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  return (
    <div className="mkt-chart-wrap">
      <svg className="mkt-chart-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
        <polyline fill="none" stroke="#f97316" strokeWidth="1.25" vectorEffect="non-scaling-stroke" points={pts.join(' ')} />
      </svg>
      <div className="mkt-chart-xlabels">
        {clean.map((c, i) => (
          <span key={i} title={`${c.label}: ${c.v}`}>{c.label.replace(/^\d{4}-/, '')}</span>
        ))}
      </div>
    </div>
  );
}

function TrafficSourceBars({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="mkt-traffic-list">
      {items.map((row, i) => {
        const pct = Number(row.pct ?? row.percent ?? 0);
        const label = row.label || row.name || '—';
        return (
          <div key={i} className="mkt-traffic-row">
            <div className="mkt-traffic-label"><span>{label}</span><span>{pct < 0.1 && pct > 0 ? '<0.1%' : `${pct}%`}</span></div>
            <div className="mkt-traffic-bar-track"><div className="mkt-traffic-bar-fill" style={{ width: `${Math.min(pct, 100)}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

const TIKTOK_STUDIO_DEMO_STATS = {
  headline: { views: 19000, likes: 496, comments: 0, shares: 5, saves: 20 },
  kpis: {
    video_views: 19000,
    total_play_time_sec: 7 * 3600 + 26 * 60 + 39,
    avg_watch_time_sec: 1.58,
    watched_full_video_pct: 0.1,
    new_followers: 10,
  },
  views_by_day: [
    { date: '2024-04-17', views: 19000 },
    { date: '2024-04-18', views: 4200 },
    { date: '2024-04-19', views: 900 },
  ],
  traffic_sources: [
    { label: 'For You', pct: 79.4 },
    { label: 'Following', pct: 11.1 },
    { label: 'Personal profile', pct: 5.7 },
    { label: 'Other', pct: 3.7 },
    { label: 'Direct messages', pct: 0.1 },
    { label: 'Search', pct: 0.05 },
    { label: 'Sound', pct: 0.05 },
  ],
  search_queries: [{ query: 'optiprime.ai', pct: 100 }],
  retention_note: 'Most viewers stopped watching at 0:01. (Demo text — replace with your TikTok Studio note.)',
};

/** In-modal preview: media + post stats snapshots (manual TikTok / LinkedIn style). */
function AssetPreviewModal({ asset, onClose }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [formPlatform, setFormPlatform] = useState('tiktok');
  const [formCaptured, setFormCaptured] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [fViews, setFViews] = useState('');
  const [fLikes, setFLikes] = useState('');
  const [fComments, setFComments] = useState('');
  const [fShares, setFShares] = useState('');
  const [fSaves, setFSaves] = useState('');
  const [fVideoViews, setFVideoViews] = useState('');
  const [fPlayH, setFPlayH] = useState('');
  const [fPlayM, setFPlayM] = useState('');
  const [fPlayS, setFPlayS] = useState('');
  const [fAvgWatch, setFAvgWatch] = useState('');
  const [fFullPct, setFFullPct] = useState('');
  const [fFollowers, setFFollowers] = useState('');
  const [fRetentionNote, setFRetentionNote] = useState('');
  const [fViewsByDayJson, setFViewsByDayJson] = useState('');
  const [fTrafficJson, setFTrafficJson] = useState('');
  const [fSearchJson, setFSearchJson] = useState('');
  const [fImp, setFImp] = useState('');
  const [fReact, setFReact] = useState('');
  const [fCommLi, setFCommLi] = useState('');
  const [fRepost, setFRepost] = useState('');
  const [fSeriesImpJson, setFSeriesImpJson] = useState('');

  // Extras (post metadata, likes_note, viewers, engagement) populated by vision LLM extraction.
  // Kept as pretty JSON so the human can review/edit before save.
  const [fExtrasJson, setFExtrasJson] = useState('');

  // Vision-assisted extract-from-screenshot state
  const [vsFiles, setVsFiles] = useState([]);
  const [vsTabHint, setVsTabHint] = useState('All tabs');
  const [vsBusy, setVsBusy] = useState(false);
  const [vsMsg, setVsMsg] = useState({ type: '', text: '' });
  const vsInputRef = useRef(null);
  const vsPasteRef = useRef(null);

  const loadSnapshots = useCallback(() => {
    if (!asset) return;
    setLoading(true);
    setLoadErr('');
    mktFetch(`${API}/assets/${asset.id}/stats`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.error || 'Failed to load stats');
        const list = d.snapshots || [];
        setSnapshots(list);
        setSelectedIdx(0);
      })
      .catch((e) => setLoadErr(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [asset]);

  useEffect(() => {
    loadSnapshots();
  }, [loadSnapshots]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const local = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setFormCaptured(local);
  }, [asset?.id]);

  const applyTikTokDemo = () => {
    setFormPlatform('tiktok');
    const d = TIKTOK_STUDIO_DEMO_STATS;
    setFViews(String(d.headline.views));
    setFLikes(String(d.headline.likes));
    setFComments(String(d.headline.comments));
    setFShares(String(d.headline.shares));
    setFSaves(String(d.headline.saves));
    setFVideoViews(String(d.kpis.video_views));
    const t = d.kpis.total_play_time_sec;
    setFPlayH(String(Math.floor(t / 3600)));
    setFPlayM(String(Math.floor((t % 3600) / 60)));
    setFPlayS(String(t % 60));
    setFAvgWatch(String(d.kpis.avg_watch_time_sec));
    setFFullPct(String(d.kpis.watched_full_video_pct));
    setFFollowers(String(d.kpis.new_followers));
    setFRetentionNote(d.retention_note || '');
    setFViewsByDayJson(JSON.stringify(d.views_by_day, null, 2));
    setFTrafficJson(JSON.stringify(d.traffic_sources, null, 2));
    setFSearchJson(JSON.stringify(d.search_queries, null, 2));
    setFormErr('');
  };

  const applyLinkedInDemo = () => {
    setFormPlatform('linkedin');
    setFImp('149');
    setFReact('1');
    setFCommLi('0');
    setFRepost('0');
    setFSeriesImpJson(JSON.stringify([
      { date: '2026-03-22', impressions: 5 },
      { date: '2026-04-15', impressions: 58 },
      { date: '2026-04-16', impressions: 12 },
    ], null, 2));
    setFViewsByDayJson('');
    setFTrafficJson('');
    setFSearchJson('');
    setFormErr('');
  };

  const parseJsonField = (text, label) => {
    const t = (text || '').trim();
    if (!t) return [];
    try {
      const v = JSON.parse(t);
      if (!Array.isArray(v)) throw new Error('must be a JSON array');
      return v;
    } catch (e) {
      throw new Error(`${label}: ${e.message}`);
    }
  };

  const parseJsonObject = (text, label) => {
    const t = (text || '').trim();
    if (!t) return null;
    try {
      const v = JSON.parse(t);
      if (!v || typeof v !== 'object' || Array.isArray(v)) throw new Error('must be a JSON object');
      return v;
    } catch (e) {
      throw new Error(`${label}: ${e.message}`);
    }
  };

  const applyExtractedToForm = (ex) => {
    if (!ex || typeof ex !== 'object') return;
    const h = ex.headline || {};
    if (h.views != null) setFViews(String(h.views));
    if (h.likes != null) setFLikes(String(h.likes));
    if (h.comments != null) setFComments(String(h.comments));
    if (h.shares != null) setFShares(String(h.shares));
    if (h.saves != null) setFSaves(String(h.saves));
    const k = ex.kpis || {};
    if (k.video_views != null) setFVideoViews(String(k.video_views));
    if (k.total_play_time_sec != null) {
      const t = Number(k.total_play_time_sec) || 0;
      setFPlayH(String(Math.floor(t / 3600)));
      setFPlayM(String(Math.floor((t % 3600) / 60)));
      setFPlayS(String(t % 60));
    }
    if (k.avg_watch_time_sec != null) setFAvgWatch(String(k.avg_watch_time_sec));
    if (k.watched_full_video_pct != null) setFFullPct(String(k.watched_full_video_pct));
    if (k.new_followers != null) setFFollowers(String(k.new_followers));
    if (ex.retention_note) setFRetentionNote(String(ex.retention_note));
    if (Array.isArray(ex.views_by_day)) setFViewsByDayJson(JSON.stringify(ex.views_by_day, null, 2));
    if (Array.isArray(ex.traffic_sources)) setFTrafficJson(JSON.stringify(ex.traffic_sources, null, 2));
    if (Array.isArray(ex.search_queries)) setFSearchJson(JSON.stringify(ex.search_queries, null, 2));

    const extras = {};
    if (ex.post) extras.post = ex.post;
    if (ex.likes_note) extras.likes_note = ex.likes_note;
    if (ex.viewers) extras.viewers = ex.viewers;
    if (ex.engagement) extras.engagement = ex.engagement;
    if (Object.keys(extras).length > 0) setFExtrasJson(JSON.stringify(extras, null, 2));
  };

  const addVisionFiles = (incoming) => {
    if (!incoming || incoming.length === 0) return 0;
    let added = 0;
    setVsFiles((prev) => {
      const room = Math.max(0, 5 - prev.length);
      const chunk = incoming.slice(0, room);
      added = chunk.length;
      return [...prev, ...chunk];
    });
    return added;
  };

  const onVisionFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const added = addVisionFiles(files);
    if (added < files.length) {
      setVsMsg({ type: 'err', text: 'Max 5 images per extract. Extra files were ignored.' });
    } else if (added > 0) {
      setVsMsg({ type: 'ok', text: `Added ${added} image(s).` });
    }
    // Clear native input so selecting the same file again still triggers onChange.
    e.target.value = '';
  };

  const onVisionPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const images = items
      .filter((it) => it.kind === 'file' && String(it.type || '').startsWith('image/'))
      .map((it, idx) => {
        const f = it.getAsFile();
        if (!f) return null;
        const ext = (f.type.split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '');
        return new File([f], f.name || `clipboard-${Date.now()}-${idx}.${ext}`, { type: f.type || 'image/png' });
      })
      .filter(Boolean);
    if (images.length === 0) return;
    e.preventDefault();
    const added = addVisionFiles(images);
    if (added < images.length) {
      setVsMsg({ type: 'err', text: 'Pasted image(s) detected, but max 5 images are allowed per extract.' });
    } else {
      setVsMsg({ type: 'ok', text: `Pasted ${added} image(s) from clipboard.` });
    }
  };

  const extractFromScreenshots = async () => {
    if (!vsFiles || vsFiles.length === 0) {
      setVsMsg({ type: 'err', text: 'Select at least one screenshot first.' });
      return;
    }
    setVsBusy(true);
    setVsMsg({ type: '', text: '' });
    try {
      const fd = new FormData();
      for (const f of vsFiles) fd.append('images', f);
      fd.append('platform', formPlatform);
      if (vsTabHint && vsTabHint !== 'All tabs') fd.append('tab_hint', vsTabHint);
      const res = await mktFetch(`${API}/assets/${asset.id}/stats/extract-image`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || res.statusText);
      applyExtractedToForm(data.extracted || {});
      setVsMsg({
        type: 'ok',
        text: `Extracted from ${data.images} image(s) via ${data.model}. Review the fields below, edit any mistakes, then Save snapshot.`,
      });
    } catch (e) {
      setVsMsg({ type: 'err', text: e.message || String(e) });
    } finally {
      setVsBusy(false);
    }
  };

  const submitSnapshot = async () => {
    setFormErr('');
    let stats;
    try {
      if (formPlatform === 'linkedin') {
        const series = parseJsonField(fSeriesImpJson, 'Impressions by day');
        stats = {
          headline: {
            impressions: Number(fImp) || 0,
            reactions: Number(fReact) || 0,
            comments: Number(fCommLi) || 0,
            reposts: Number(fRepost) || 0,
          },
          series_impressions: series,
        };
      } else {
        const viewsByDay = parseJsonField(fViewsByDayJson, 'views_by_day');
        const traffic = parseJsonField(fTrafficJson, 'traffic_sources');
        const searchQ = parseJsonField(fSearchJson, 'search_queries');
        const ph = Number(fPlayH) || 0;
        const pm = Number(fPlayM) || 0;
        const ps = Number(fPlayS) || 0;
        stats = {
          headline: {
            views: Number(fViews) || 0,
            likes: Number(fLikes) || 0,
            comments: Number(fComments) || 0,
            shares: Number(fShares) || 0,
            saves: Number(fSaves) || 0,
          },
          kpis: {
            video_views: Number(fVideoViews) || Number(fViews) || 0,
            total_play_time_sec: ph * 3600 + pm * 60 + ps,
            avg_watch_time_sec: Number(fAvgWatch) || 0,
            watched_full_video_pct: Number(fFullPct) || 0,
            new_followers: Number(fFollowers) || 0,
          },
          views_by_day: viewsByDay,
          traffic_sources: traffic,
          search_queries: searchQ,
          retention_note: fRetentionNote.trim() || undefined,
        };
        const extras = parseJsonObject(fExtrasJson, 'Extras (post / viewers / engagement)');
        if (extras) {
          if (extras.post) stats.post = extras.post;
          if (extras.likes_note) stats.likes_note = extras.likes_note;
          if (extras.viewers) stats.viewers = extras.viewers;
          if (extras.engagement) stats.engagement = extras.engagement;
        }
      }
    } catch (e) {
      setFormErr(e.message || String(e));
      return;
    }

    setSaving(true);
    try {
      const res = await mktFetch(`${API}/assets/${asset.id}/stats`, {
        method: 'POST',
        body: JSON.stringify({
          platform: formPlatform,
          captured_at: formCaptured || null,
          notes: formNotes.trim() || null,
          stats,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || res.statusText);
      setShowForm(false);
      setFormNotes('');
      loadSnapshots();
    } catch (e) {
      setFormErr(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteSnapshot = async (snap) => {
    if (!window.confirm('Delete this stats snapshot?')) return;
    try {
      const res = await mktFetch(`${API}/assets/${asset.id}/stats/${snap.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || res.statusText);
      }
      loadSnapshots();
    } catch (e) {
      setLoadErr(e.message || String(e));
    }
  };

  if (!asset) return null;

  const snap = snapshots[selectedIdx] || null;
  const stats = normalizeStats(snap?.stats);
  const headline = stats.headline || {};
  const kpis = stats.kpis || {};
  const isLi = (snap?.platform || '') === 'linkedin';
  const series = pickSeries(stats);

  const fileUrl = `${API}/assets/${asset.id}/file`;

  return (
    <div className="mkt-viewer-overlay" role="dialog" aria-modal="true" aria-labelledby="mkt-viewer-title" onClick={onClose}>
      <div className="mkt-viewer-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="mkt-viewer-head">
          <div>
            <h3 id="mkt-viewer-title" className="mkt-viewer-title">{asset.filename}</h3>
            <p className="mkt-viewer-sub">Post performance snapshots — enter numbers from TikTok Studio or LinkedIn (manual; not auto-synced).</p>
          </div>
          <div className="mkt-viewer-head-actions">
            <a className="mkt-btn-sm mkt-btn-link" href={fileUrl} target="_blank" rel="noopener noreferrer">Open file in new tab</a>
            {asset.source_url && (
              <a className="mkt-btn-sm mkt-btn-link" href={asset.source_url} target="_blank" rel="noopener noreferrer">Original post</a>
            )}
            <button type="button" className="mkt-btn-sm mkt-btn-ghost-sm" onClick={onClose}>Close</button>
          </div>
        </div>

        <div className="mkt-viewer-body">
          <div className="mkt-viewer-media">
            {asset.kind === 'video' && (
              <video className="mkt-viewer-video" src={fileUrl} controls playsInline preload="metadata" />
            )}
            {asset.kind === 'image' && (
              <img className="mkt-viewer-image" src={fileUrl} alt={asset.filename} />
            )}
            {(asset.kind !== 'video' && asset.kind !== 'image') && (
              <div className="mkt-viewer-nomedia">
                <p>No inline preview for this file type.</p>
                <a className="mkt-btn-sm mkt-btn-link" href={fileUrl} target="_blank" rel="noopener noreferrer">Download / open</a>
              </div>
            )}
          </div>

          <div className="mkt-viewer-stats">
            <div className="mkt-viewer-stats-toolbar">
              <label className="mkt-stat-select-label">
                Snapshot
                <select
                  className="mkt-select mkt-select-inline"
                  value={selectedIdx}
                  onChange={(e) => setSelectedIdx(Number(e.target.value))}
                  disabled={snapshots.length === 0}
                >
                  {snapshots.length === 0 ? (
                    <option value={0}>No snapshots yet</option>
                  ) : (
                    snapshots.map((s, i) => (
                      <option key={s.id} value={i}>
                        {new Date(s.captured_at).toLocaleString()} · {s.platform}
                        {s.notes ? ` — ${String(s.notes).slice(0, 40)}` : ''}
                      </option>
                    ))
                  )}
                </select>
              </label>
              <button type="button" className="mkt-btn-sm" onClick={() => setShowForm((v) => !v)}>
                {showForm ? 'Cancel add' : '+ Add snapshot'}
              </button>
              {snap && (
                <button type="button" className="mkt-btn-sm mkt-btn-ghost-sm" onClick={() => deleteSnapshot(snap)}>Delete snapshot</button>
              )}
            </div>

            {loading && <p className="mkt-stat-muted">Loading…</p>}
            {loadErr && <p className="mkt-save-msg mkt-save-msg-err">{loadErr}</p>}

            {showForm && (
              <div className="mkt-stat-form">
                <p className="mkt-stat-form-hint">
                  Paste figures from TikTok Studio / LinkedIn analytics — or upload a screenshot and let the on-prem vision model (<code>qwen2.5vl</code>) extract them.
                  A human always has the final save (you can edit everything below).
                  {' '}<button type="button" className="mkt-btn-inline-link" onClick={applyTikTokDemo}>Fill TikTok demo</button>
                  {' · '}
                  <button type="button" className="mkt-btn-inline-link" onClick={applyLinkedInDemo}>Fill LinkedIn demo</button>
                </p>

                {formPlatform === 'tiktok' && (
                  <div
                    className="mkt-vision-block"
                    ref={vsPasteRef}
                    tabIndex={0}
                    onPaste={onVisionPaste}
                    title="Click here, then press Ctrl+V to paste screenshot(s)"
                  >
                    <div className="mkt-vision-title">Auto-fill from screenshot(s)</div>
                    <p className="mkt-vision-hint">
                      Upload or paste (Ctrl+V) 1–5 TikTok Studio screenshots (Overview, Viewers, Engagement). Images are sent to the local Ollama vision model —
                      never leave this server. The extracted JSON is written into the fields below for you to review and edit.
                    </p>
                    <div className="mkt-vision-row">
                      <label className="mkt-stat-field">
                        Tab
                        <select className="mkt-select" value={vsTabHint} onChange={(e) => setVsTabHint(e.target.value)}>
                          <option>All tabs</option>
                          <option>Overview</option>
                          <option>Viewers</option>
                          <option>Engagement</option>
                        </select>
                      </label>
                      <input
                        className="mkt-file-input"
                        ref={vsInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={onVisionFileChange}
                      />
                      <button type="button" className="mkt-btn-sm" onClick={extractFromScreenshots} disabled={vsBusy || vsFiles.length === 0}>
                        {vsBusy ? 'Extracting…' : `Extract ${vsFiles.length || ''}`.trim()}
                      </button>
                      <button type="button" className="mkt-btn-ghost-sm" onClick={() => setVsFiles([])} disabled={vsBusy || vsFiles.length === 0}>
                        Clear images
                      </button>
                    </div>
                    {vsFiles.length > 0 && (
                      <p className="mkt-stat-muted">Selected: {vsFiles.map((f) => f.name).join(', ')}</p>
                    )}
                    {vsMsg.text && (
                      <p className={vsMsg.type === 'err' ? 'mkt-save-msg mkt-save-msg-err' : 'mkt-save-msg mkt-save-msg-ok'}>
                        {vsMsg.text}
                      </p>
                    )}
                  </div>
                )}
                <div className="mkt-stat-form-row">
                  <label className="mkt-stat-field">
                    Platform
                    <select className="mkt-select" value={formPlatform} onChange={(e) => setFormPlatform(e.target.value)}>
                      <option value="tiktok">TikTok</option>
                      <option value="linkedin">LinkedIn</option>
                    </select>
                  </label>
                  <label className="mkt-stat-field">
                    Captured at
                    <input className="mkt-input" type="datetime-local" value={formCaptured} onChange={(e) => setFormCaptured(e.target.value)} />
                  </label>
                </div>
                <label className="mkt-stat-field mkt-stat-field-full">
                  Notes (optional)
                  <input className="mkt-input" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="e.g. Week 1 after boost" />
                </label>

                {formPlatform === 'tiktok' ? (
                  <>
                    <h4 className="mkt-stat-section-title">Headline counts</h4>
                    <div className="mkt-stat-form-grid">
                      <label>Views<input className="mkt-input" inputMode="numeric" value={fViews} onChange={(e) => setFViews(e.target.value)} /></label>
                      <label>Likes<input className="mkt-input" inputMode="numeric" value={fLikes} onChange={(e) => setFLikes(e.target.value)} /></label>
                      <label>Comments<input className="mkt-input" inputMode="numeric" value={fComments} onChange={(e) => setFComments(e.target.value)} /></label>
                      <label>Shares<input className="mkt-input" inputMode="numeric" value={fShares} onChange={(e) => setFShares(e.target.value)} /></label>
                      <label>Saves<input className="mkt-input" inputMode="numeric" value={fSaves} onChange={(e) => setFSaves(e.target.value)} /></label>
                    </div>
                    <h4 className="mkt-stat-section-title">KPIs</h4>
                    <div className="mkt-stat-form-grid">
                      <label>Video views<input className="mkt-input" inputMode="numeric" value={fVideoViews} onChange={(e) => setFVideoViews(e.target.value)} placeholder="same as views if blank" /></label>
                      <label className="mkt-stat-span-3">Total play time (h:m:s)
                        <span className="mkt-stat-hms">
                          <input className="mkt-input" inputMode="numeric" value={fPlayH} onChange={(e) => setFPlayH(e.target.value)} placeholder="h" />
                          <input className="mkt-input" inputMode="numeric" value={fPlayM} onChange={(e) => setFPlayM(e.target.value)} placeholder="m" />
                          <input className="mkt-input" inputMode="numeric" value={fPlayS} onChange={(e) => setFPlayS(e.target.value)} placeholder="s" />
                        </span>
                      </label>
                      <label>Avg watch (s)<input className="mkt-input" inputMode="decimal" value={fAvgWatch} onChange={(e) => setFAvgWatch(e.target.value)} /></label>
                      <label>Full watch %<input className="mkt-input" inputMode="decimal" value={fFullPct} onChange={(e) => setFFullPct(e.target.value)} /></label>
                      <label>New followers<input className="mkt-input" inputMode="numeric" value={fFollowers} onChange={(e) => setFFollowers(e.target.value)} /></label>
                    </div>
                    <label className="mkt-stat-field mkt-stat-field-full">Retention note (optional)
                      <textarea className="mkt-input mkt-textarea" rows={2} value={fRetentionNote} onChange={(e) => setFRetentionNote(e.target.value)} />
                    </label>
                    <label className="mkt-stat-field mkt-stat-field-full">views_by_day JSON (optional)
                      <textarea className="mkt-input mkt-textarea mkt-textarea-mono" rows={4} value={fViewsByDayJson} onChange={(e) => setFViewsByDayJson(e.target.value)} placeholder='[{"date":"2024-04-17","views":19000},…]' />
                    </label>
                    <label className="mkt-stat-field mkt-stat-field-full">traffic_sources JSON (optional)
                      <textarea className="mkt-input mkt-textarea mkt-textarea-mono" rows={3} value={fTrafficJson} onChange={(e) => setFTrafficJson(e.target.value)} placeholder='[{"label":"For You","pct":79.4},…]' />
                    </label>
                    <label className="mkt-stat-field mkt-stat-field-full">search_queries JSON (optional)
                      <textarea className="mkt-input mkt-textarea mkt-textarea-mono" rows={2} value={fSearchJson} onChange={(e) => setFSearchJson(e.target.value)} placeholder='[{"query":"optiprime.ai","pct":100}]' />
                    </label>
                    <label className="mkt-stat-field mkt-stat-field-full">
                      Extras JSON — post / likes_note / viewers / engagement (populated by vision extract; editable)
                      <textarea className="mkt-input mkt-textarea mkt-textarea-mono" rows={10}
                        value={fExtrasJson} onChange={(e) => setFExtrasJson(e.target.value)}
                        placeholder='{ "post": {...}, "likes_note": "...", "viewers": { "total": 6000, "gender": {...}, "age": [...], "locations": [...] }, "engagement": {...} }' />
                    </label>
                  </>
                ) : (
                  <>
                    <h4 className="mkt-stat-section-title">LinkedIn highlights</h4>
                    <div className="mkt-stat-form-grid">
                      <label>Impressions<input className="mkt-input" inputMode="numeric" value={fImp} onChange={(e) => setFImp(e.target.value)} /></label>
                      <label>Reactions<input className="mkt-input" inputMode="numeric" value={fReact} onChange={(e) => setFReact(e.target.value)} /></label>
                      <label>Comments<input className="mkt-input" inputMode="numeric" value={fCommLi} onChange={(e) => setFCommLi(e.target.value)} /></label>
                      <label>Reposts<input className="mkt-input" inputMode="numeric" value={fRepost} onChange={(e) => setFRepost(e.target.value)} /></label>
                    </div>
                    <label className="mkt-stat-field mkt-stat-field-full">series_impressions JSON (optional, for graph)
                      <textarea className="mkt-input mkt-textarea mkt-textarea-mono" rows={5} value={fSeriesImpJson} onChange={(e) => setFSeriesImpJson(e.target.value)} placeholder='[{"date":"2026-04-15","impressions":58},…]' />
                    </label>
                  </>
                )}
                {formErr && <p className="mkt-save-msg mkt-save-msg-err">{formErr}</p>}
                <button type="button" className="mkt-btn" disabled={saving} onClick={submitSnapshot}>{saving ? 'Saving…' : 'Save snapshot'}</button>
              </div>
            )}

            {!loading && !showForm && snapshots.length === 0 && (
              <p className="mkt-stat-muted">No stats recorded yet. Click <strong>+ Add snapshot</strong> and paste numbers from TikTok Studio.</p>
            )}

            {!loading && snap && (
              <div className="mkt-stat-display">
                {stats.post && (stats.post.title || stats.post.posted_on) && (
                  <div className="mkt-stat-post-meta">
                    {stats.post.title && <div className="mkt-stat-post-title">{stats.post.title}</div>}
                    {stats.post.posted_on && <div className="mkt-stat-post-date">Posted: {stats.post.posted_on}</div>}
                  </div>
                )}
                <div className="mkt-stat-pills">
                  {isLi ? (
                    <>
                      <span className="mkt-stat-pill"><em>Impressions</em>{fmtCompactNum(headline.impressions)}</span>
                      <span className="mkt-stat-pill"><em>Reactions</em>{fmtCompactNum(headline.reactions)}</span>
                      <span className="mkt-stat-pill"><em>Comments</em>{fmtCompactNum(headline.comments)}</span>
                      <span className="mkt-stat-pill"><em>Reposts</em>{fmtCompactNum(headline.reposts)}</span>
                    </>
                  ) : (
                    <>
                      <span className="mkt-stat-pill"><em>Views</em>{fmtCompactNum(headline.views)}</span>
                      <span className="mkt-stat-pill"><em>Likes</em>{fmtCompactNum(headline.likes)}</span>
                      <span className="mkt-stat-pill"><em>Comments</em>{fmtCompactNum(headline.comments)}</span>
                      <span className="mkt-stat-pill"><em>Shares</em>{fmtCompactNum(headline.shares)}</span>
                      <span className="mkt-stat-pill"><em>Saves</em>{fmtCompactNum(headline.saves)}</span>
                    </>
                  )}
                </div>

                {!isLi && (
                  <div className="mkt-stat-kpi-row">
                    <div className="mkt-stat-kpi"><span>Video views</span><strong>{fmtCompactNum(kpis.video_views)}</strong></div>
                    <div className="mkt-stat-kpi"><span>Total play time</span><strong>{fmtPlayTime(kpis.total_play_time_sec)}</strong></div>
                    <div className="mkt-stat-kpi"><span>Avg watch</span><strong>{kpis.avg_watch_time_sec != null ? `${kpis.avg_watch_time_sec}s` : '—'}</strong></div>
                    <div className="mkt-stat-kpi"><span>Watched full</span><strong>{kpis.watched_full_video_pct != null ? `${kpis.watched_full_video_pct}%` : '—'}</strong></div>
                    <div className="mkt-stat-kpi"><span>New followers</span><strong>{fmtCompactNum(kpis.new_followers)}</strong></div>
                  </div>
                )}

                {stats.retention_note && (
                  <p className="mkt-stat-retention-note">{stats.retention_note}</p>
                )}

                <h4 className="mkt-stat-section-title">{series.yKey === 'impressions' ? 'Impressions over time' : 'Video views over time'}</h4>
                <MiniLineChart points={series.points} yKey={series.yKey} />

                {!isLi && stats.traffic_sources && stats.traffic_sources.length > 0 && (
                  <>
                    <h4 className="mkt-stat-section-title">Traffic source</h4>
                    <TrafficSourceBars items={stats.traffic_sources} />
                  </>
                )}

                {!isLi && stats.search_queries && stats.search_queries.length > 0 && (
                  <>
                    <h4 className="mkt-stat-section-title">Search queries</h4>
                    <TrafficSourceBars items={stats.search_queries.map((q) => ({ label: q.query || q.q, pct: q.pct }))} />
                  </>
                )}

                {!isLi && stats.viewers && (
                  <>
                    <h4 className="mkt-stat-section-title">
                      Viewers{stats.viewers.total != null ? ` — ${fmtCompactNum(stats.viewers.total)} total` : ''}
                    </h4>
                    {stats.viewers.types && (
                      <TrafficSourceBars items={[
                        { label: 'New viewers', pct: stats.viewers.types.new_viewers_pct },
                        { label: 'Returning viewers', pct: stats.viewers.types.returning_viewers_pct },
                        { label: 'Non-followers', pct: stats.viewers.types.non_followers_pct },
                        { label: 'Followers', pct: stats.viewers.types.followers_pct },
                      ].filter((x) => x.pct != null)} />
                    )}
                    {stats.viewers.gender && (
                      <>
                        <h4 className="mkt-stat-subsection-title">Gender</h4>
                        <TrafficSourceBars items={[
                          { label: 'Male', pct: stats.viewers.gender.male_pct },
                          { label: 'Female', pct: stats.viewers.gender.female_pct },
                          { label: 'Other', pct: stats.viewers.gender.other_pct },
                        ].filter((x) => x.pct != null)} />
                      </>
                    )}
                    {Array.isArray(stats.viewers.age) && stats.viewers.age.length > 0 && (
                      <>
                        <h4 className="mkt-stat-subsection-title">Age</h4>
                        <TrafficSourceBars items={stats.viewers.age.map((a) => ({ label: a.range, pct: a.pct }))} />
                      </>
                    )}
                    {Array.isArray(stats.viewers.locations) && stats.viewers.locations.length > 0 && (
                      <>
                        <h4 className="mkt-stat-subsection-title">Locations</h4>
                        <TrafficSourceBars items={stats.viewers.locations.map((l) => ({ label: l.country, pct: l.pct }))} />
                      </>
                    )}
                  </>
                )}

                {!isLi && (stats.likes_note || stats.engagement) && (
                  <>
                    <h4 className="mkt-stat-section-title">Engagement</h4>
                    {stats.likes_note && <p className="mkt-stat-retention-note">{stats.likes_note}</p>}
                    {stats.engagement && Array.isArray(stats.engagement.top_words_in_comments) && stats.engagement.top_words_in_comments.length > 0 && (
                      <TrafficSourceBars items={stats.engagement.top_words_in_comments.map((w) => ({ label: w.word || w.label, pct: w.pct }))} />
                    )}
                    {stats.engagement && stats.engagement.top_words_note && !stats.engagement.top_words_in_comments?.length && (
                      <p className="mkt-stat-muted">{stats.engagement.top_words_note}</p>
                    )}
                  </>
                )}

                {snap.notes && <p className="mkt-stat-notes"><strong>Note:</strong> {snap.notes}</p>}

                {snapshots.length > 1 && (
                  <div className="mkt-stat-history">
                    <h4 className="mkt-stat-section-title">All captures (views / impressions)</h4>
                    <ul>
                      {snapshots.map((s) => {
                        const st = normalizeStats(s.stats);
                        const hv = st.headline?.views ?? st.headline?.impressions;
                        return (
                          <li key={s.id}>
                            {new Date(s.captured_at).toLocaleDateString()} — {fmtCompactNum(hv)} ({s.platform})
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Agents Panel ─────────────────────────────────────────────────────────────
function AgentsPanel({ onChatWith }) {
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    mktFetch(API + '/subagents').then(r => r.json()).then(d => setAgents(d.subagents || []));
  }, []);

  const grouped = [0, 1, 2, 3].map(tier => ({
    tier,
    items: agents.filter(a => a.tier === tier),
  }));

  return (
    <div className="mkt-panel">
      <h3 className="mkt-panel-heading">Marketing Department — Sub-Agents</h3>
      <p className="mkt-panel-sub">Click any agent to open a conversation in the Chat tab.</p>
      {grouped.map(({ tier, items }) => items.length > 0 && (
        <div key={tier} className="mkt-agent-tier-group">
          <div className="mkt-agent-tier-label" style={{ color: TIER_COLOUR[tier] }}>
            {TIER_LABEL[tier]}
          </div>
          <div className="mkt-agent-cards">
            {items.map(a => (
              <button key={a.id} type="button" className="mkt-agent-card" onClick={() => onChatWith(a.id)}>
                <div className="mkt-agent-card-name" style={{ color: TIER_COLOUR[tier] }}>{a.name}</div>
                <div className="mkt-agent-card-desc">{a.description}</div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Campaigns Panel ───────────────────────────────────────────────────────────
function CampaignsPanel({ onPreviewAsset }) {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [showNew, setShowNew]   = useState(false);
  const [form, setForm] = useState({ slug: '', name: '', channel: '', platform: '', notes: '', source_url: '', goal: '' });
  const [sourceUrlEdit, setSourceUrlEdit] = useState('');
  const [goalEdit, setGoalEdit] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [campaignLinkMsg, setCampaignLinkMsg] = useState({ type: '', text: '' });
  const [campaignGoalMsg, setCampaignGoalMsg] = useState({ type: '', text: '' });
  const [campaignSummaryMsg, setCampaignSummaryMsg] = useState({ type: '', text: '' });
  const [summaryHeight, setSummaryHeight] = useState(360);
  const summaryDragRef = useRef({ dragging: false, startY: 0, startH: 360 });

  const load = useCallback(() => {
    mktFetch(API + '/campaigns').then(r => r.json()).then(d => setCampaigns(d.campaigns || []));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    mktFetch(API + '/campaigns/' + selected)
      .then(r => r.json()).then(d => setDetail(d));
  }, [selected]);

  useEffect(() => {
    if (detail && detail.campaign) {
      setSourceUrlEdit(detail.campaign.source_url || '');
      setGoalEdit(detail.campaign.goal || '');
    }
  }, [detail]);

  const save = async () => {
    if (!form.slug || !form.name) return;
    setSaving(true);
    await mktFetch(API + '/campaigns', { method: 'POST', body: JSON.stringify(form) });
    setSaving(false);
    setShowNew(false);
    setForm({ slug: '', name: '', channel: '', platform: '', notes: '', source_url: '', goal: '' });
    load();
  };

  const saveCampaignSourceUrl = async () => {
    if (!detail || !detail.campaign) return;
    const c = detail.campaign;
    setCampaignLinkMsg({ type: '', text: '' });
    setSavingLink(true);
    try {
      const payload = {
        slug: c.slug,
        name: c.name,
        channel: c.channel,
        platform: c.platform,
        notes: c.notes,
        launched_at: c.launched_at,
        spend_cents: c.spend_cents != null ? Number(c.spend_cents) : 0,
        currency: c.currency || 'CAD',
        source_url: sourceUrlEdit.trim() || null,
      };
      const res = await mktFetch(API + '/campaigns', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCampaignLinkMsg({ type: 'err', text: data.error || res.statusText || 'Save failed' });
        return;
      }
      const d = await mktFetch(API + '/campaigns/' + c.slug).then((r) => r.json());
      setDetail(d);
      setCampaignLinkMsg({ type: 'ok', text: 'Link saved to this campaign in the database.' });
    } catch (e) {
      setCampaignLinkMsg({ type: 'err', text: e.message || 'Network error' });
    } finally {
      setSavingLink(false);
    }
  };

  const saveCampaignGoal = async () => {
    if (!detail || !detail.campaign) return;
    const c = detail.campaign;
    setCampaignGoalMsg({ type: '', text: '' });
    setSavingGoal(true);
    try {
      const payload = {
        slug: c.slug,
        name: c.name,
        channel: c.channel,
        platform: c.platform,
        notes: c.notes,
        launched_at: c.launched_at,
        spend_cents: c.spend_cents != null ? Number(c.spend_cents) : 0,
        currency: c.currency || 'CAD',
        source_url: c.source_url || null,
        goal: goalEdit.trim() || null,
      };
      const res = await mktFetch(API + '/campaigns', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCampaignGoalMsg({ type: 'err', text: data.error || res.statusText || 'Save failed' });
        return;
      }
      const d = await mktFetch(API + '/campaigns/' + c.slug).then((r) => r.json());
      setDetail(d);
      setCampaignGoalMsg({ type: 'ok', text: 'Campaign goal saved.' });
    } catch (e) {
      setCampaignGoalMsg({ type: 'err', text: e.message || 'Network error' });
    } finally {
      setSavingGoal(false);
    }
  };

  const generateCampaignSummary = async () => {
    if (!detail || !detail.campaign) return;
    setCampaignSummaryMsg({ type: '', text: '' });
    setGeneratingSummary(true);
    try {
      const res = await mktFetch(`${API}/campaigns/${detail.campaign.slug}/generate-summary`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCampaignSummaryMsg({ type: 'err', text: data.error || res.statusText || 'Generate failed' });
        return;
      }
      const d = await mktFetch(API + '/campaigns/' + detail.campaign.slug).then((r) => r.json());
      setDetail(d);
      setCampaignSummaryMsg({
        type: 'ok',
        text: `Summary generated with ${data.model}. Reviewed ${data.counts?.assets || 0} assets and ${data.counts?.snapshots || 0} snapshots.`,
      });
    } catch (e) {
      setCampaignSummaryMsg({ type: 'err', text: e.message || 'Network error' });
    } finally {
      setGeneratingSummary(false);
    }
  };

  return (
    <div className="mkt-panel mkt-two-col">
      <div className="mkt-list-col">
        <div className="mkt-list-header">
          <h3 className="mkt-panel-heading">Campaigns</h3>
          <button type="button" className="mkt-btn-sm" onClick={() => setShowNew(v => !v)}>+ New</button>
        </div>
        {showNew && (
          <div className="mkt-new-form">
            <input className="mkt-input" placeholder="Slug (e.g. tiktok-may-2026)" value={form.slug}
              onChange={e => setForm(v => ({ ...v, slug: e.target.value }))} />
            <input className="mkt-input" placeholder="Name" value={form.name}
              onChange={e => setForm(v => ({ ...v, name: e.target.value }))} />
            <input className="mkt-input" placeholder="Channel (paid / organic)" value={form.channel}
              onChange={e => setForm(v => ({ ...v, channel: e.target.value }))} />
            <input className="mkt-input" placeholder="Platform (TikTok / Instagram...)" value={form.platform}
              onChange={e => setForm(v => ({ ...v, platform: e.target.value }))} />
            <input className="mkt-input" placeholder="Original post URL (TikTok, optional)" value={form.source_url}
              onChange={e => setForm(v => ({ ...v, source_url: e.target.value }))} />
            <textarea className="mkt-input mkt-textarea" placeholder="Campaign goal (optional)" value={form.goal}
              onChange={e => setForm(v => ({ ...v, goal: e.target.value }))} rows={2} />
            <textarea className="mkt-input mkt-textarea" placeholder="Notes" value={form.notes}
              onChange={e => setForm(v => ({ ...v, notes: e.target.value }))} rows={2} />
            <div className="mkt-form-actions">
              <button type="button" className="mkt-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              <button type="button" className="mkt-btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
            </div>
          </div>
        )}
        {campaigns.length === 0 && <p className="mkt-empty">No campaigns yet.</p>}
        {campaigns.map(c => (
          <button key={c.id} type="button"
            className={`mkt-list-item${selected === c.slug ? ' mkt-list-item-active' : ''}`}
            onClick={() => setSelected(c.slug === selected ? null : c.slug)}>
            <span className="mkt-list-item-name">{c.name}</span>
            <span className="mkt-list-item-meta">{c.platform} · {c.channel}</span>
          </button>
        ))}
      </div>
      <div className="mkt-detail-col">
        {!detail && <p className="mkt-empty mkt-empty-center">Select a campaign to see details.</p>}
        {detail && (
          <div className="mkt-campaign-detail">
            <h3 className="mkt-detail-title">{detail.campaign.name}</h3>
            <div className="mkt-kv-grid">
              <span>Platform</span><span>{detail.campaign.platform || '—'}</span>
              <span>Channel</span><span>{detail.campaign.channel || '—'}</span>
              <span>Currency</span><span>{detail.campaign.currency || 'CAD'}</span>
              <span>Launched</span><span>{detail.campaign.launched_at ? new Date(detail.campaign.launched_at).toLocaleDateString() : '—'}</span>
              <span>Notes</span><span>{detail.campaign.notes || '—'}</span>
            </div>
            <div className="mkt-campaign-source-block">
              <label className="mkt-field-label" htmlFor="mkt-campaign-source-url">Original post URL (TikTok or other)</label>
              <div className="mkt-source-row">
                <input
                  id="mkt-campaign-source-url"
                  className="mkt-input mkt-input-inline"
                  placeholder="https://www.tiktok.com/@…/video/…"
                  value={sourceUrlEdit}
                  onChange={(e) => setSourceUrlEdit(e.target.value)}
                />
                <button type="button" className="mkt-btn-sm" onClick={saveCampaignSourceUrl} disabled={savingLink}>
                  {savingLink ? 'Saving…' : 'Save link'}
                </button>
              </div>
              {detail.campaign.source_url && (
                <a className="mkt-external-link" href={detail.campaign.source_url} target="_blank" rel="noopener noreferrer">
                  Open saved link
                </a>
              )}
              {campaignLinkMsg.text && (
                <p className={campaignLinkMsg.type === 'err' ? 'mkt-save-msg mkt-save-msg-err' : 'mkt-save-msg mkt-save-msg-ok'}>
                  {campaignLinkMsg.text}
                </p>
              )}
            </div>
            <div className="mkt-campaign-goal-block">
              <label className="mkt-field-label" htmlFor="mkt-campaign-goal">Campaign goal</label>
              <p className="mkt-campaign-goal-hint">
                Define the measurable objective this agent should optimize for (e.g., target views, CTR, follows, CPC, conversions, timeline).
              </p>
              <textarea
                id="mkt-campaign-goal"
                className="mkt-input mkt-textarea"
                rows={3}
                placeholder="Example: Reach 50K TikTok views and 250 profile visits in 30 days with CPC under $0.60."
                value={goalEdit}
                onChange={(e) => setGoalEdit(e.target.value)}
              />
              <div className="mkt-form-actions">
                <button type="button" className="mkt-btn-sm" onClick={saveCampaignGoal} disabled={savingGoal}>
                  {savingGoal ? 'Saving…' : 'Save goal'}
                </button>
                <button type="button" className="mkt-btn-sm" onClick={generateCampaignSummary} disabled={generatingSummary}>
                  {generatingSummary ? 'Generating…' : 'Generate summary'}
                </button>
              </div>
              {campaignGoalMsg.text && (
                <p className={campaignGoalMsg.type === 'err' ? 'mkt-save-msg mkt-save-msg-err' : 'mkt-save-msg mkt-save-msg-ok'}>
                  {campaignGoalMsg.text}
                </p>
              )}
              {campaignSummaryMsg.text && (
                <p className={campaignSummaryMsg.type === 'err' ? 'mkt-save-msg mkt-save-msg-err' : 'mkt-save-msg mkt-save-msg-ok'}>
                  {campaignSummaryMsg.text}
                </p>
              )}
            </div>

            <div className="mkt-campaign-summary-block">
              <div className="mkt-campaign-summary-head">
                <h4 className="mkt-section-title">Generated summary</h4>
              </div>
              {detail.campaign.summary ? (
                <>
                  {detail.campaign.summary_updated_at && (
                    <p className="mkt-stat-muted">Updated: {new Date(detail.campaign.summary_updated_at).toLocaleString()}</p>
                  )}
                  {(() => {
                    const s = parseSummaryJson(detail.campaign.summary);
                    if (!s || typeof s !== 'object') {
                      return (
                        <div className="mkt-campaign-summary-text" style={{ maxHeight: `${summaryHeight}px` }}>
                          {prettifySummaryText(detail.campaign.summary)}
                        </div>
                      );
                    }
                    const list = (arr) => Array.isArray(arr) ? arr : [];
                    return (
                      <div className="mkt-campaign-summary-json" style={{ maxHeight: `${summaryHeight}px` }}>
                        {s.goal && (
                          <div className="mkt-campaign-summary-card">
                            <div className="mkt-campaign-summary-card-title">Goal</div>
                            <div className="mkt-campaign-summary-card-body">{String(s.goal)}</div>
                          </div>
                        )}
                        <div className="mkt-campaign-summary-card">
                          <div className="mkt-campaign-summary-card-title">Progress vs Goal</div>
                          {list(s.progress_vs_goal).map((x, i) => <div key={i} className="mkt-campaign-summary-line">- {String(x)}</div>)}
                        </div>
                        <div className="mkt-campaign-summary-card">
                          <div className="mkt-campaign-summary-card-title">What Is Working</div>
                          {list(s.what_is_working).map((x, i) => <div key={i} className="mkt-campaign-summary-line">- {String(x)}</div>)}
                        </div>
                        <div className="mkt-campaign-summary-card">
                          <div className="mkt-campaign-summary-card-title">Risks</div>
                          {list(s.risks).map((x, i) => <div key={i} className="mkt-campaign-summary-line">- {String(x)}</div>)}
                        </div>
                        <div className="mkt-campaign-summary-card">
                          <div className="mkt-campaign-summary-card-title">Next Best Actions (7 days)</div>
                          {list(s.actions_7d).map((x, i) => (
                            <div key={i} className="mkt-campaign-summary-line">
                              - [{x.priority || 'P2'}] {x.action || '—'} ({x.success_metric || 'metric not set'})
                            </div>
                          ))}
                        </div>
                        <div className="mkt-campaign-summary-card">
                          <div className="mkt-campaign-summary-card-title">Next Best Actions (30 days)</div>
                          {list(s.actions_30d).map((x, i) => (
                            <div key={i} className="mkt-campaign-summary-line">
                              - [{x.priority || 'P2'}] {x.action || '—'} ({x.success_metric || 'metric not set'})
                            </div>
                          ))}
                        </div>
                        <div className="mkt-campaign-summary-card">
                          <div className="mkt-campaign-summary-card-title">Weekly KPI Targets</div>
                          {list(s.weekly_kpis).map((x, i) => (
                            <div key={i} className="mkt-campaign-summary-line">
                              - {x.kpi || 'KPI'}: {x.target || 'target n/a'} ({x.why || 'reason n/a'})
                            </div>
                          ))}
                        </div>
                        {list(s.references_used).length > 0 && (
                          <div className="mkt-campaign-summary-card">
                            <div className="mkt-campaign-summary-card-title">References Used</div>
                            {list(s.references_used).map((x, i) => (
                              <div key={i} className="mkt-campaign-summary-line">
                                - {x.source || 'Source'}: {x.how_applied || 'n/a'}
                              </div>
                            ))}
                          </div>
                        )}
                        {list(s.data_gaps).length > 0 && (
                          <div className="mkt-campaign-summary-card">
                            <div className="mkt-campaign-summary-card-title">Data Gaps</div>
                            {list(s.data_gaps).map((x, i) => <div key={i} className="mkt-campaign-summary-line">- {String(x)}</div>)}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div
                    className="mkt-summary-resizer"
                    role="separator"
                    aria-label="Resize summary panel"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      summaryDragRef.current = { dragging: true, startY: e.clientY, startH: summaryHeight };
                      const onMove = (ev) => {
                        if (!summaryDragRef.current.dragging) return;
                        const dy = ev.clientY - summaryDragRef.current.startY;
                        const next = Math.max(220, Math.min(window.innerHeight * 0.78, summaryDragRef.current.startH + dy));
                        setSummaryHeight(Math.round(next));
                      };
                      const onUp = () => {
                        summaryDragRef.current.dragging = false;
                        window.removeEventListener('mousemove', onMove);
                        window.removeEventListener('mouseup', onUp);
                      };
                      window.addEventListener('mousemove', onMove);
                      window.addEventListener('mouseup', onUp);
                    }}
                  >
                    <span />
                    <span />
                    <span />
                  </div>
                </>
              ) : (
                <p className="mkt-empty">No generated summary yet.</p>
              )}
            </div>

            <div className="mkt-campaign-snapshots-block">
              <h4 className="mkt-section-title">Snapshot summaries ({detail.snapshots?.length || 0})</h4>
              {(!detail.snapshots || detail.snapshots.length === 0) && (
                <p className="mkt-empty">No snapshots saved yet for this campaign.</p>
              )}
              {detail.snapshots && detail.snapshots.map((s) => {
                const st = normalizeStats(s.stats);
                const h = st.headline || {};
                const k = st.kpis || {};
                return (
                  <div key={s.id} className="mkt-campaign-snapshot-row">
                    <div className="mkt-campaign-snapshot-head">
                      <strong>{new Date(s.captured_at).toLocaleString()}</strong>
                      <span>{s.filename || `Asset ${s.asset_id}`} · {s.platform}</span>
                    </div>
                    <div className="mkt-campaign-snapshot-metrics">
                      <span>Views: {fmtCompactNum(h.views ?? k.video_views)}</span>
                      <span>Likes: {fmtCompactNum(h.likes)}</span>
                      <span>Comments: {fmtCompactNum(h.comments)}</span>
                      <span>Shares: {fmtCompactNum(h.shares)}</span>
                      <span>Saves: {fmtCompactNum(h.saves)}</span>
                      <span>Avg watch: {k.avg_watch_time_sec != null ? `${k.avg_watch_time_sec}s` : '—'}</span>
                      <span>Full watch: {k.watched_full_video_pct != null ? `${k.watched_full_video_pct}%` : '—'}</span>
                      <span>New followers: {fmtCompactNum(k.new_followers)}</span>
                    </div>
                    {s.notes && <p className="mkt-stat-muted">Note: {s.notes}</p>}
                  </div>
                );
              })}
            </div>
            <h4 className="mkt-section-title">Assets ({detail.assets.length})</h4>
            {detail.assets.length === 0 && <p className="mkt-empty">No assets for this campaign.</p>}
            {detail.assets.map(a => (
              <div key={a.id} className="mkt-asset-row mkt-asset-row-campaign">
                <span className={`mkt-kind-badge mkt-kind-${a.kind}`}>{a.kind}</span>
                <span className="mkt-asset-name">{a.filename}</span>
                <span className="mkt-asset-meta">{fmtBytes(a.size_bytes)}</span>
                {onPreviewAsset && (
                  <button type="button" className="mkt-btn-sm" onClick={() => onPreviewAsset(a)}>
                    Preview + stats
                  </button>
                )}
                <a
                  className="mkt-btn-sm mkt-btn-link"
                  href={`${API}/assets/${a.id}/file`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View file
                </a>
                {a.source_url && (
                  <a className="mkt-btn-sm mkt-btn-link" href={a.source_url} target="_blank" rel="noopener noreferrer">
                    TikTok / post
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Assets Panel ──────────────────────────────────────────────────────────────
function AssetsPanel({ onPreviewAsset }) {
  const [assets, setAssets]             = useState([]);
  const [campaigns, setCampaigns]       = useState([]);
  const [uploadCampaign, setUploadCampaign] = useState('');
  const [uploadSourceUrl, setUploadSourceUrl] = useState('');
  const [uploading, setUploading]       = useState(false);
  const [uploadMsg, setUploadMsg]       = useState('');
  const [txLog, setTxLog]               = useState({});   // assetId → lines[]
  const [txRunning, setTxRunning]       = useState({});   // assetId → bool
  const [transcript, setTranscript]     = useState(null); // { assetId, text }
  const [linkDrafts, setLinkDrafts]     = useState({});    // assetId → draft URL (undefined = use server)
  const [savingAssetLink, setSavingAssetLink] = useState({});
  const [assetLinkMsg, setAssetLinkMsg] = useState({ id: null, type: '', text: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [manageBusy, setManageBusy] = useState({});
  const fileRef = useRef(null);

  const assetSourceDraft = (a) => (linkDrafts[a.id] !== undefined ? linkDrafts[a.id] : (a.source_url || ''));

  const manageAsset = async (asset, action, body) => {
    setManageBusy((s) => ({ ...s, [asset.id]: true }));
    try {
      const method = action === 'delete' ? 'DELETE' : 'POST';
      const url = action === 'delete'
        ? `${API}/platform/assets/${asset.id}`
        : `${API}/platform/assets/${asset.id}/${action}`;
      const res = await mktFetch(url, { method, body: body ? JSON.stringify(body) : undefined });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAssetLinkMsg({ id: asset.id, type: 'err', text: data.error || `${action} failed` });
        return;
      }
      await load();
    } catch (e) {
      setAssetLinkMsg({ id: asset.id, type: 'err', text: e.message });
    } finally {
      setManageBusy((s) => ({ ...s, [asset.id]: false }));
    }
  };

  const load = useCallback(async () => {
    const [ar, cr] = await Promise.all([
      mktFetch(API + '/assets').then(r => r.json()),
      mktFetch(API + '/campaigns').then(r => r.json()),
    ]);
    setAssets(ar.assets || []);
    setCampaigns(cr.campaigns || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const uploadFile = async () => {
    const f = fileRef.current && fileRef.current.files[0];
    if (!f) { setUploadMsg('Select a file first.'); return; }
    setUploading(true);
    setUploadMsg('Uploading…');
    const fd = new FormData();
    fd.append('file', f);
    if (uploadCampaign) fd.append('campaign_slug', uploadCampaign);
    fd.append('agent_id', 'creative_production');
    if (uploadSourceUrl.trim()) fd.append('source_url', uploadSourceUrl.trim());
    try {
      const res = await mktFetch(API + '/assets/upload', { method: 'POST', body: fd });
      const data = await res.json();
      if (data.asset) {
        setUploadMsg('Uploaded: ' + data.asset.filename);
        fileRef.current.value = '';
        setUploadSourceUrl('');
        load();
      } else {
        setUploadMsg('Error: ' + (data.error || 'Upload failed'));
      }
    } catch (e) {
      setUploadMsg('Error: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  const transcribe = async (asset) => {
    if (txRunning[asset.id]) return;
    setTxLog(l => ({ ...l, [asset.id]: ['Starting transcription…'] }));
    setTxRunning(r => ({ ...r, [asset.id]: true }));
    setTranscript(null);

    try {
      const res = await mktFetch(API + '/assets/' + asset.id + '/transcribe', {
        method: 'POST',
        body: JSON.stringify({ model: 'large-v3', language: 'auto' }),
      });
      if (!res.body) throw new Error('No response body');

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const raw of lines) {
          const line = raw.trim();
          if (!line) continue;
          if (line.startsWith('data:')) {
            const payload = line.slice(5).trim();
            try {
              const parsed = JSON.parse(payload);
              if (parsed.token) continue; // chat tokens — ignore here
              if (parsed.done === true || parsed.word_count != null) {
                const msg = parsed.word_count
                  ? `Done — ${parsed.word_count} words, ${fmtDuration(parsed.duration_sec)}, language: ${parsed.language}`
                  : 'Done.';
                setTxLog(l => ({ ...l, [asset.id]: [...(l[asset.id] || []), msg] }));
                load();
              } else if (parsed.error) {
                setTxLog(l => ({ ...l, [asset.id]: [...(l[asset.id] || []), 'Error: ' + parsed.error] }));
              }
            } catch (_) {
              setTxLog(l => ({ ...l, [asset.id]: [...(l[asset.id] || []), payload] }));
            }
          } else if (line.startsWith('event:')) {
            // handled via next data line
          }
        }
      }
    } catch (e) {
      setTxLog(l => ({ ...l, [asset.id]: [...(l[asset.id] || []), 'Error: ' + e.message] }));
    } finally {
      setTxRunning(r => ({ ...r, [asset.id]: false }));
    }
  };

  const viewTranscript = async (asset) => {
    if (transcript && transcript.assetId === asset.id) { setTranscript(null); return; }
    const res = await mktFetch(API + '/assets/' + asset.id + '/transcript?model=large-v3');
    if (!res.ok) { setTranscript({ assetId: asset.id, text: '(no transcript yet)', error: true }); return; }
    const d = await res.json();
    setTranscript({ assetId: asset.id, text: d.transcript.text, meta: d.transcript });
  };

  const saveAssetSourceUrl = async (asset) => {
    const url = assetSourceDraft(asset).trim();
    setAssetLinkMsg({ id: null, type: '', text: '' });
    setSavingAssetLink((s) => ({ ...s, [asset.id]: true }));
    try {
      const res = await mktFetch(API + '/assets/' + asset.id, {
        method: 'PATCH',
        body: JSON.stringify({ source_url: url || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAssetLinkMsg({ id: asset.id, type: 'err', text: data.error || res.statusText || 'Save failed' });
        return;
      }
      setLinkDrafts((d) => {
        const n = { ...d };
        delete n[asset.id];
        return n;
      });
      await load();
      setAssetLinkMsg({
        id: asset.id,
        type: 'ok',
        text: url ? 'Link saved on this asset (marketing_assets.source_url in optiprime_main).' : 'Link cleared.',
      });
    } catch (e) {
      setAssetLinkMsg({ id: asset.id, type: 'err', text: e.message || 'Network error' });
    } finally {
      setSavingAssetLink((s) => ({ ...s, [asset.id]: false }));
    }
  };

  return (
    <div className="mkt-panel">
      {/* Upload bar */}
      <div className="mkt-upload-bar">
        <h3 className="mkt-panel-heading">Assets</h3>
        <p className="mkt-panel-sub mkt-upload-hint">
          Upload files are stored under OptiPrime AI Inc/06_marketing/assets/. TikTok does not allow reliable server-side download here — paste the original post URL when you upload (or add it below for each asset).
        </p>
        <div className="mkt-upload-controls">
          <select className="mkt-select" value={uploadCampaign} onChange={e => setUploadCampaign(e.target.value)}>
            <option value="">No campaign</option>
            {campaigns.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
          </select>
          <input type="file" ref={fileRef} accept="video/*,image/*,.mp4,.mov,.avi,.jpg,.jpeg,.png,.gif,.webp" className="mkt-file-input" />
          <button type="button" className="mkt-btn" onClick={uploadFile} disabled={uploading}>{uploading ? 'Uploading…' : 'Upload'}</button>
        </div>
        <input
          className="mkt-input mkt-upload-source"
          placeholder="Original TikTok / social post URL (optional, saved with this upload)"
          value={uploadSourceUrl}
          onChange={(e) => setUploadSourceUrl(e.target.value)}
        />
        {uploadMsg && <p className="mkt-upload-msg">{uploadMsg}</p>}
      </div>

      {/* Status filter */}
      <div className="mkt-asset-filter-bar">
        {[
          { id: 'all', label: 'All' },
          { id: 'live', label: 'Live' },
          { id: 'saved', label: 'Saved ★' },
          { id: 'archived', label: 'Archived' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`mkt-asset-filter-tab${statusFilter === t.id ? ' is-active' : ''}`}
            onClick={() => setStatusFilter(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Asset list */}
      {assets.length === 0 && <p className="mkt-empty">No assets yet. Upload a video or image above.</p>}
      <div className="mkt-assets-list">
        {assets
          .filter((a) => (a.status || 'active') !== 'purged')
          .filter((a) => {
            if (statusFilter === 'all') return true;
            if (statusFilter === 'saved') return a.starred || a.status === 'saved';
            if (statusFilter === 'archived') return a.status === 'archived';
            if (statusFilter === 'live') return a.status === 'active' || a.status === 'kept';
            return true;
          })
          .map(a => (
          <div key={a.id} className="mkt-asset-block">
            <div className="mkt-asset-block-header">
              <span className={`mkt-kind-badge mkt-kind-${a.kind}`}>{a.kind}</span>
              {a.status && a.status !== 'active' && (
                <span className={`mkt-asset-status mkt-asset-status-${a.starred ? 'saved' : a.status}`}>
                  {a.starred ? 'Saved' : a.status}
                  {a.status === 'archived' && !a.starred ? ' · auto-delete 30d' : ''}
                </span>
              )}
              <span className="mkt-asset-block-name">{a.filename}</span>
              <span className="mkt-asset-block-size">{fmtBytes(a.size_bytes)}</span>
              <div className="mkt-asset-actions">
                <button
                  type="button"
                  className="mkt-btn-sm"
                  disabled={manageBusy[a.id]}
                  title={a.starred ? 'Remove from library' : 'Save to library (never auto-deleted)'}
                  onClick={() => manageAsset(a, 'star', { starred: !a.starred })}
                >
                  {a.starred ? '★ Saved' : '☆ Save'}
                </button>
                {a.status === 'archived' && (
                  <button type="button" className="mkt-btn-sm" disabled={manageBusy[a.id]} onClick={() => manageAsset(a, 'restore')}>
                    Restore
                  </button>
                )}
                <button type="button" className="mkt-btn-sm mkt-btn-ghost-sm" disabled={manageBusy[a.id]} onClick={() => manageAsset(a, 'delete')}>
                  Delete
                </button>
                <button
                  type="button"
                  className="mkt-btn-sm"
                  onClick={() => onPreviewAsset && onPreviewAsset(a)}
                >
                  Preview + stats
                </button>
                <a
                  className="mkt-btn-sm mkt-btn-link"
                  href={`${API}/assets/${a.id}/file`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View file
                </a>
                {a.source_url && (
                  <a className="mkt-btn-sm mkt-btn-link" href={a.source_url} target="_blank" rel="noopener noreferrer">
                    Original post
                  </a>
                )}
                {(a.kind === 'video' || a.kind === 'audio') && (
                  <button type="button" className="mkt-btn-sm" disabled={txRunning[a.id]}
                    onClick={() => transcribe(a)}>
                    {txRunning[a.id] ? 'Transcribing…' : 'Transcribe'}
                  </button>
                )}
                {(a.kind === 'video' || a.kind === 'audio') && (
                  <button type="button" className="mkt-btn-sm mkt-btn-ghost-sm"
                    onClick={() => viewTranscript(a)}>
                    {transcript && transcript.assetId === a.id ? 'Hide' : 'Transcript'}
                  </button>
                )}
              </div>
            </div>

            <div className="mkt-asset-source-row">
              <input
                className="mkt-input mkt-input-tight"
                placeholder="Original TikTok / post URL for this file"
                value={assetSourceDraft(a)}
                onChange={(e) => setLinkDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
              />
              <button
                type="button"
                className="mkt-btn-sm"
                disabled={savingAssetLink[a.id]}
                onClick={() => saveAssetSourceUrl(a)}
              >
                {savingAssetLink[a.id] ? '…' : 'Save link'}
              </button>
            </div>
            {assetLinkMsg.id === a.id && assetLinkMsg.text && (
              <p className={assetLinkMsg.type === 'err' ? 'mkt-save-msg mkt-save-msg-err mkt-save-msg-asset' : 'mkt-save-msg mkt-save-msg-ok mkt-save-msg-asset'}>
                {assetLinkMsg.text}
              </p>
            )}

            {/* Transcription log */}
            {txLog[a.id] && txLog[a.id].length > 0 && (
              <div className="mkt-tx-log">
                {txLog[a.id].map((line, i) => <div key={i} className="mkt-tx-log-line">{line}</div>)}
              </div>
            )}

            {/* Transcript viewer */}
            {transcript && transcript.assetId === a.id && (
              <div className="mkt-transcript-view">
                {transcript.meta && (
                  <div className="mkt-transcript-meta">
                    <span>{transcript.meta.word_count} words</span>
                    <span>{fmtDuration(transcript.meta.duration_sec)}</span>
                    <span>Lang: {transcript.meta.language}</span>
                    <span>Engine: {transcript.meta.engine} {transcript.meta.model}</span>
                  </div>
                )}
                <pre className="mkt-transcript-text">{transcript.text}</pre>
              </div>
            )}
          </div>
        ))}
      </div>

    </div>
  );
}

// ── Chat Panel ────────────────────────────────────────────────────────────────
function ChatPanel({ initialAgent }) {
  const [agents, setAgents]         = useState([]);
  const [subAgent, setSubAgent]     = useState(initialAgent || '');
  const [campaigns, setCampaigns] = useState([]);
  const [chatCampaignSlug, setChatCampaignSlug] = useState('');
  const [chatIncludeTx, setChatIncludeTx] = useState(true);
  const [history, setHistory]       = useState([]);
  const [input, setInput]           = useState('');
  const [streaming, setStreaming]   = useState(false);
  const [streamBuf, setStreamBuf]   = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    mktFetch(API + '/subagents').then(r => r.json()).then(d => {
      setAgents(d.subagents || []);
      if (!subAgent && d.subagents && d.subagents.length > 0) setSubAgent(d.subagents[0].id);
    });
  }, []);

  useEffect(() => {
    mktFetch(API + '/campaigns').then(r => r.json()).then((d) => {
      const list = d.campaigns || [];
      setCampaigns(list);
      setChatCampaignSlug((prev) => {
        if (prev) return prev;
        const pref = list.find((c) => c.slug === 'tiktok-paid-apr-2026');
        return (pref || list[0] || {}).slug || '';
      });
    });
  }, []);

  useEffect(() => {
    if (initialAgent) setSubAgent(initialAgent);
  }, [initialAgent]);

  useEffect(() => {
    if (!subAgent) return;
    setHistory([]);
    mktFetch(API + '/history?subAgent=' + subAgent)
      .then(r => r.json())
      .then(d => setHistory(d.history || []));
  }, [subAgent]);

  useEffect(() => {
    bottomRef.current && bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [history, streamBuf]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || streaming || !subAgent) return;
    setInput('');
    setStreaming(true);
    setStreamBuf('');
    setHistory(h => [...h, { role: 'user', content: msg, created_at: new Date().toISOString() }]);

    try {
      const res = await mktFetch(API + '/chat', {
        method: 'POST',
        body: JSON.stringify({
          subAgent,
          message: msg,
          campaignSlug: chatCampaignSlug || undefined,
          includeTranscripts: chatIncludeTx,
        }),
      });
      if (!res.ok || !res.body) throw new Error(res.statusText || 'No response body');

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let full = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const raw of lines) {
          const line = raw.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const parsed = JSON.parse(line.slice(5).trim());
            if (parsed.token) { full += parsed.token; setStreamBuf(full); }
          } catch (_) {}
        }
      }

      setHistory(h => [...h, { role: 'assistant', content: full, created_at: new Date().toISOString() }]);
      setStreamBuf('');
    } catch (e) {
      setHistory(h => [...h, { role: 'assistant', content: '⚠ ' + e.message, created_at: new Date().toISOString() }]);
      setStreamBuf('');
    } finally {
      setStreaming(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const agentDef = agents.find(a => a.id === subAgent);

  return (
    <div className="mkt-chat-layout">
      <div className="mkt-chat-topbar">
        <select className="mkt-select" value={subAgent} onChange={e => setSubAgent(e.target.value)}>
          {agents.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        {agentDef && <span className="mkt-chat-agent-desc">{agentDef.description}</span>}
      </div>
      <div className="mkt-chat-help">
        <strong>Who receives your message?</strong>{' '}
        The dropdown selects one of the 11 marketing sub-agents (coordinator + 10 specialists). It is{' '}
        <em>not</em> a single “manager” bar: each agent has its own role, system prompt, and conversation history in PostgreSQL.
        The default on first load is <strong>Marketing Coordinator</strong> — use that for briefs that span multiple specialists;
        switch to Social Media, Paid Ads, Content, etc. for channel-specific work. Replies stream from the local{' '}
        <strong>Ollama</strong> model configured on the server (default: qwen2.5:32b-instruct-q4_K_M).
        {' '}<strong>Transcripts:</strong> Ollama cannot read folders on disk. Turn on <em>Attach transcripts</em> below so the
        server loads text from <code>marketing_transcripts</code> (PostgreSQL) for the selected campaign into this chat turn.
      </div>

      <div className="mkt-chat-context-bar">
        <label className="mkt-chat-context-label">
          <input
            type="checkbox"
            checked={chatIncludeTx}
            onChange={(e) => setChatIncludeTx(e.target.checked)}
          />
          Attach transcripts for this turn
        </label>
        <select
          className="mkt-select mkt-select-chat"
          value={chatCampaignSlug}
          onChange={(e) => setChatCampaignSlug(e.target.value)}
          disabled={!chatIncludeTx}
          title="Scope transcript text to one campaign, or all recent assets"
        >
          <option value="">All recent (any campaign)</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.slug}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="mkt-chat-messages">
        {history.length === 0 && !streamBuf && (
          <p className="mkt-empty mkt-empty-center">No messages yet. Start a conversation.</p>
        )}
        {history.map((m, i) => (
          <div key={i} className={`mkt-msg mkt-msg-${m.role}`}>
            <span className="mkt-msg-role">{m.role === 'user' ? 'You' : agentDef ? agentDef.name : 'Agent'}</span>
            <pre className="mkt-msg-text">{m.content}</pre>
          </div>
        ))}
        {streamBuf && (
          <div className="mkt-msg mkt-msg-assistant mkt-msg-streaming">
            <span className="mkt-msg-role">{agentDef ? agentDef.name : 'Agent'}</span>
            <pre className="mkt-msg-text">{streamBuf}<span className="mkt-cursor">▍</span></pre>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="mkt-chat-input-bar">
        <textarea
          className="mkt-chat-input"
          placeholder={`Message ${agentDef ? agentDef.name : 'agent'}… (Enter to send, Shift+Enter for newline)`}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={streaming}
          rows={2}
        />
        <button type="button" className="mkt-btn mkt-send-btn" onClick={send} disabled={streaming || !input.trim()}>
          {streaming ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
const MarketingHubModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab]     = useState('agents');
  const [chatAgent, setChatAgent]     = useState(null);
  const [previewAsset, setPreviewAsset] = useState(null);

  const openChat = (agentId) => {
    setChatAgent(agentId);
    setActiveTab('chat');
  };

  if (!isOpen) return null;

  return (
    <div className="mkt-hub-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="mkt-hub-box" onClick={e => e.stopPropagation()}>
        <div className="mkt-hub-header">
          <h2 className="mkt-hub-title">Marketing</h2>
          <span className="mkt-hub-subtitle">OptiPrime AI Inc. — Marketing Department</span>
          <button type="button" className="mkt-hub-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="mkt-hub-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`mkt-hub-tab${activeTab === tab.id ? ' mkt-hub-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="mkt-hub-content">
          {activeTab === 'agents'    && <AgentsPanel    onChatWith={openChat} />}
          {activeTab === 'campaigns' && <CampaignsPanel onPreviewAsset={setPreviewAsset} />}
          {activeTab === 'assets'    && <AssetsPanel onPreviewAsset={setPreviewAsset} />}
          {activeTab === 'chat'      && <ChatPanel initialAgent={chatAgent} />}
        </div>
      </div>
      {previewAsset && (
        <AssetPreviewModal asset={previewAsset} onClose={() => setPreviewAsset(null)} />
      )}
    </div>
  );
};

export default MarketingHubModal;
export {
  AgentsPanel,
  CampaignsPanel,
  AssetsPanel,
  ChatPanel,
  AssetPreviewModal,
  mktFetch,
  API as MARKETING_API,
};
